import List from "components/list";
import { useParams } from "react-router-dom";
import { usePermissions } from "contexts/permission";
import { UserCell } from "components/cell/user/cell";
import { getUserFields } from "components/user/config";
import { Button } from "ui/index";
import { Icon } from "components/index";
import { EDIT } from "icons/src";
import EmployeeInput from "components/employee/input";
import { useMutation } from "@tanstack/react-query";
import { updateItemInCollection } from "directus/src/operations";
import { invalidateAllQueries } from "utils/utils";
import { eventBus } from "utils/eventBus";
import { toast } from "sonner";
import { UserFieldInput } from "permissions/components/user-field-input";
import { ROUTE } from "@/lib/utils";

export default function TeamLeadList() {
  const { employeeId } = useParams();
  const permissions = usePermissions();
  const canEditReportsTo = permissions.hasFieldAccess("employees", "update", "reports_to");
  const userFilter = employeeId ? { _eq: employeeId } : { user: { _eq: "$CURRENT_USER" } };

  const storageKey = employeeId ? `profile-team-lead-${employeeId}` : "profile-team-lead";

  const updateReportsToMutation = useMutation({
    mutationFn: async ({ id, reportsToId }) => {
      await updateItemInCollection("employees", id, { reports_to: reportsToId });
    },
    onSuccess: () => {
      invalidateAllQueries();
      toast.success("Team Lead Updated Successfully");
      eventBus.emit("refresh:list", `employees${storageKey}`);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const fields = [
    "id",
    ...getUserFields(),
    getUserFields("reports_to"),
    "function.name",
    "employee_id",
  ];

  const columns = [
    {
      id: "employee",
      header: "Employee",
      accessorKey: "first_name",
      cell: UserCell,
    },
    {
      id: "reports_to",
      header: "Reports to",
      hidden: !canEditReportsTo,
      href: false,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <UserFieldInput
            targetItem={item.id}
            field="reports_to"
            collection="employees"
            showWhenDisabled={false}
          >
            <EmployeeInput
              set="all"
              value={item.reports_to}
              singleSelect={true}
              footer={({ setOpen, selectedEmployees }) => (
                <Button
                  onClick={async () => {
                    const selectedEmployee = selectedEmployees?.[0];
                    if (selectedEmployee) {
                      await updateReportsToMutation.mutateAsync({
                        id: item.id,
                        reportsToId: selectedEmployee.id,
                      });
                    }
                    setOpen(false);
                  }}
                >
                  Assign
                </Button>
              )}
            >
              <Button variant="ghost">
                <Icon icon={EDIT} />
              </Button>
            </EmployeeInput>
          </UserFieldInput>
        );
      }
    }
  ];

  const filters = {
    reports_to: userFilter,
    user: { status: { _eq: "active" } },
    type: { _neq: "client" },
  };

  return (
    <div>
      <List
        title="Team Lead"
        storageKey={storageKey}
        endPoint="employees"
        filters={filters}
        columns={columns.filter(col => !col.hidden)}
        meta={{ fields }}
        sortBy="first_name"
        href={(item) => ROUTE.EMPLOYEE(item.id)}
      />
    </div>
  );
}
